import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { AlertTriangleIcon } from "lucide-react";
import {
  formatDateLocal,
  formatDateTimeLocal,
} from "@/shared/lib/date-time-performance";
import type { EventDateConflictItemDTO } from "../../domain/contracts/event-date-conflicts";

const EVENT_STATUS_LABELS = {
  draft: "ກຳລັງວາງແຜນ",
  confirmed: "ຍືນຍັນແລ້ວ",
  completed: "ສຳເລັດ",
  cancelled: "ຍົກເລີກ",
} as const;

const REQUEST_STATUS_LABELS = {
  new: "ໃໝ່",
  negotiating: "ກຳລັງເຈລະຈາ",
  pending_quote: "ລໍຖ້າໃບສະເໜີລາຄາ",
  won: "ຢືນຢັນແລ້ວ",
  lost: "ເສຍໂອກາດ",
  cancelled: "ຍົກເລີກ",
} as const;

function formatEventRange(
  eventDate: Date,
  endDate: Date | null,
): string {
  if (!endDate) {
    return formatDateTimeLocal(eventDate);
  }

  const sameDay = formatDateLocal(eventDate) === formatDateLocal(endDate);
  if (sameDay) {
    return `${formatDateLocal(eventDate)} ${format(eventDate, "HH:mm")} – ${format(endDate, "HH:mm")}`;
  }

  return `${formatDateTimeLocal(eventDate)} – ${formatDateTimeLocal(endDate)}`;
}

function formatConflictMeta(item: EventDateConflictItemDTO): string {
  if (item.kind === "event") {
    return `${formatEventRange(item.eventDate, item.endDate)} · ${EVENT_STATUS_LABELS[item.status]}`;
  }
  return `${formatDateTimeLocal(item.requestedEventDate)} · ${REQUEST_STATUS_LABELS[item.status]}`;
}

type EventDateConflictAlertProps = {
  conflicts: EventDateConflictItemDTO[];
  isChecking?: boolean;
};

export function EventDateConflictAlert({
  conflicts,
  isChecking = false,
}: EventDateConflictAlertProps) {
  if (conflicts.length === 0) {
    return null;
  }

  return (
    <div
      role="alert"
      className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-amber-950 text-sm dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
    >
      <div className="flex gap-2">
        <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0">
          <p className="font-semibold">
            ວັນທີຊ້ຳກັນກັບງານ/ຄິວອື່ນ
            {isChecking ? " …" : ""}
          </p>
          <p className="mt-0.5 text-xs opacity-90">
            ເທົ່າວັນໃນປະຕິທິນ (Asia/Bangkok) · ຍັງບັນທຶກໄດ້
          </p>
          <ul className="mt-2 list-none space-y-1.5 text-xs">
            {conflicts.map((item) => (
              <li key={`${item.kind}:${item.id}`}>
                <span className="font-medium text-amber-950/70 dark:text-amber-100/70">
                  {item.kind === "request" ? "ຄິວ" : "ງານ"}:{" "}
                </span>
                {item.kind === "event" ? (
                  <Link
                    to="/app/performance/events/$id"
                    params={{ id: item.id }}
                    className="font-medium underline-offset-2 hover:underline"
                  >
                    {item.title}
                  </Link>
                ) : (
                  <Link
                    to="/app/performance/requests/$id"
                    params={{ id: item.id }}
                    className="font-medium underline-offset-2 hover:underline"
                  >
                    {item.title}
                  </Link>
                )}
                <span className="text-amber-900/80 dark:text-amber-100/80">
                  {" "}
                  · {formatConflictMeta(item)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
